import React, { useEffect, useState, lazy, Suspense, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../style.css";
import axios from "axios";
import { Search01Icon } from "hugeicons-react";
import bgImageLg from "../../../Assets/home_banner_lg.webp";
import bgImageSm from "../../../Assets/home_banner_sm.webp";

// Lazy load non-critical components
const CustomDateRangePicker = lazy(
  () => import("../../../Components/CustomDateRangePicker"),
);

const NewBanner = () => {
  const navigate = useNavigate();
  const guestReff1 = useRef(null);
  const guestReff2 = useRef(null);
  const [location, setLocation] = useState("");
  const [visible, setVisible] = useState(false);
  const [guestCounts, setGuestCounts] = useState({
    guest: 0,
    child: 0,
    infants: 0,
  });
  const [section, setSection] = useState({
    imageUrl:
      "https://365luxuryhomes.s3.ap-southeast-1.amazonaws.com/sections/1745480853407_image2.webp",
  });
  const [loading] = useState(false);

  const [isSmall, setIsSmall] = useState(window.innerWidth <= 640);

  const gtagSendEvent = (callback) => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "search_click", {
        event_category: "Search",
        event_label: location || "unknown",
        event_callback: callback,
        event_timeout: 2000,
      });
    } else {
      callback();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmall(window.innerWidth <= 640);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Preload the background image
    const img = new Image();
    img.src = section.imageUrl;

    fetchSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOutside = (event) => {
    const isClickOutsideFirst =
      guestReff1.current && !guestReff1.current.contains(event.target);
    const isClickOutsideSecond =
      guestReff2.current && !guestReff2.current.contains(event.target);

    if (isClickOutsideFirst && isClickOutsideSecond) {
      setVisible(false);
    }
  };

  useEffect(() => {
    // Listen for both mouse and touch events
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const fetchSection = async () => {
    try {
      const response = await axios.get(
        "http://365luxuryhomes.com/api/sections/home_page",
      );
      if (response.data?.imageUrl) {
        setSection(response.data);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCounter = (value, type) => {
    setGuestCounts((prev) => ({
      ...prev,
      [type]: value === "plus" ? prev[type] + 1 : Math.max(0, prev[type] - 1),
    }));
  };

  const getGuestDisplayText = () => {
    const { guest, child, infants } = guestCounts;
    if (!guest && !child && !infants) return "";

    return [
      guest > 0 ? `${guest} Guest` : "",
      child > 0 ? `${child} Child` : "",
      infants > 0 ? `${infants} Infants` : "",
    ]
      .filter(Boolean)
      .join(", ");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const locationSlug = location
      ? location.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")
      : "";
    const url = locationSlug
      ? `/short-term-rentals/${locationSlug}`
      : "/short-term-rentals";
    const params = new URLSearchParams();
    if (guestCounts.guest) params.append("guest", guestCounts.guest);
    if (guestCounts.child) params.append("child", guestCounts.child);
    if (guestCounts.infants) params.append("infants", guestCounts.infants);
    const queryString = params.toString();

    gtagSendEvent(() => {
      navigate(queryString ? `${url}?${queryString}` : url);
    });
  };

  return (
    <section
      className="home_banner relative  py-12"
      style={{
        backgroundImage: `url(${isSmall ? bgImageSm : bgImageLg})`,
        backgroundSize: "cover",
        backgroundPosition: "left",
      }}
    >
      {loading && (
        <div className="skeleton h-full w-full absolute top-0 left-0 -z-"></div>
      )}
      <div className="container h-[100%]">
        <div className="md:h-2/3 h-3/4 flex flex-col justify-end text-white items-center pb-12">
          <h1 className="text-center md:text-5xl text-3xl font-bold mb-2">
            Premium Vacation
            <br /> Rentals in Dubai
          </h1>
          <p className="text-center">
            Experience Unmatched Comfort in Dubai’s Luxury Holiday Homes
          </p>
        </div>
        <div className="flex flex-col justify-end items-center md:h-1/3 h-auto">
          <form
            onSubmit={handleSubmit}
            className="w-full p-6 px-12 bg-white float-end lg:flex md:flex hidden items-center justify-center gap-4 mb-5 rounded-lg lg:mt-0 md:mt-0 mt-32"
          >
            <div className="lg:grid grid-cols-4 flex lg:flex-row md:flex-row flex-col items-end justify-center gap-4">
              <div className="w-full">
                <select
                  value={location}
                  required
                  onChange={(e) => setLocation(e.target.value)}
                  className="select border-b-2 w-full min-w-[250px] text-[#74716d] border-b-[#aca9a5] h-[50px] rounded-none max-w-xl focus:outline-0 focus:border-b-2 focus:border-b-[#aca9a5]"
                >
                  <option selected>Where do you want to stay?</option>
                  <option value={"Palm Jumeirah"}>Palm Jumeirah</option>
                  <option value={"Dubai Marina"}>Dubai Marina</option>
                  <option value={"Downtown"}>Downtown</option>
                  <option value={"Bluewaters Island"}>Bluewaters Island</option>
                  <option value={"JBR"}>Jumeirah Beach Residence (JBR)</option>
                  <option value={"Emaar Beachfront"}>Emaar Beachfront</option>
                  <option value={"Jumeirah Village Circle"}>JVC</option>
                  <option value={"District One"}>District One</option>
                  <option value={"Dubai"}>Dubai</option>
                </select>
              </div>
              <Suspense fallback={<div>Loading...</div>}>
                <CustomDateRangePicker />
              </Suspense>
              <div className="relative w-full rounded-md h-[52px]">
                <input
                  type="text"
                  name=""
                  placeholder="Select Guests"
                  id="guest-field"
                  value={getGuestDisplayText()}
                  onClick={() => setVisible(!visible)}
                  className="block min-w-[140px] text-[16px] text-[#74716d] w-full h-[52px] max-w-md px-3 py-2 border border-gray-300 focus:outline-none focus:ring-[#aca9a5] placeholder:text-[#74716d] focus:border-[#aca9a5] rounded-md"
                />

                {visible && (
                  <div
                    id="guest-counter"
                    ref={guestReff1}
                    className="bg-white p-4 shadow-md absolute top-14 w-60 rounded-lg z-20"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <span className="text-[16px] leading-none font-medium text-[#74716d] block">
                            Adult
                          </span>
                          <span className="text-xs text-[#74716d]">
                            Ages 13 or above
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <div
                            onClick={() => handleCounter("minus", "guest")}
                            className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-[#74716d]"
                              fill={"none"}
                            >
                              <path
                                d="M20 12L4 12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-[#74716d]">
                            {guestCounts.guest}
                          </span>
                          <div
                            onClick={() => handleCounter("plus", "guest")}
                            className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-[#74716d]"
                              fill={"none"}
                            >
                              <path
                                d="M12 4V20"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M4 12H20"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <span className="text-[16px] leading-none font-medium text-[#74716d] block">
                            Children
                          </span>
                          <span className="text-xs text-[#74716d]">
                            Ages 2 to 12
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <div
                            onClick={() => handleCounter("minus", "child")}
                            className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-[#74716d]"
                              fill={"none"}
                            >
                              <path
                                d="M20 12L4 12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-[#74716d]">
                            {guestCounts.child}
                          </span>
                          <div
                            onClick={() => handleCounter("plus", "child")}
                            className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-[#74716d]"
                              fill={"none"}
                            >
                              <path
                                d="M12 4V20"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M4 12H20"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <span className="text-[16px] leading-none font-medium text-[#74716d] block">
                            Infants
                          </span>
                          <span className="text-xs text-[#74716d]">
                            Under 2 years
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <div
                            onClick={() => handleCounter("minus", "infants")}
                            className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-[#74716d]"
                              fill={"none"}
                            >
                              <path
                                d="M20 12L4 12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-[#74716d]">
                            {guestCounts.infants}
                          </span>
                          <div
                            onClick={() => handleCounter("plus", "infants")}
                            className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-[#74716d]"
                              fill={"none"}
                            >
                              <path
                                d="M12 4V20"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M4 12H20"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <span
                        onClick={() => setVisible(!visible)}
                        className="text-[#74716d] text-[16px] cursor-pointer mt-4 flex items-center gap-1 hover:text-red-500"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width={20}
                          height={20}
                          className="text-inherit"
                          fill={"none"}
                        >
                          <path
                            d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Close
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                id="searchBtn"
                className="mb-[2px] lg:w-auto md:w-auto lg:px-12 md:px-8 px-6 rounded-md w-full h-[50px] bg-[#4d4b47]
        text-white font-normal border-none hover:bg-gray-400 flex items-center justify-center gap-2"
              >
                <Search01Icon size={20} />
                Search
              </button>
            </div>
          </form>
          <div
            onClick={() => document.getElementById("my_modal_3").showModal()}
            className="flex items-center gap-4 mb-8 lg:hidden md:hidden transition-all ease-in-out hover:scale-95 w-full"
          >
            <div className="w-full h-[50px] bg-white rounded-xl text-[#74716d] flex items-center p-4 gap-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={24}
                height={24}
                className="text-inherit"
                fill={"none"}
              >
                <path
                  d="M17.5 17.5L22 22"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 11C20 6.02944 15.9706 2 11 2C6.02944 2 2 6.02944 2 11C2 15.9706 6.02944 20 11 20C15.9706 20 20 15.9706 20 11Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex flex-col items-start">
                <span className="leading-none text-[#74716d]">Where to?</span>
                <span className="text-[10px] text-[#74716d]">
                  Anywhere • Any week • Add guests
                </span>
              </div>
            </div>
            <div className="bg-white p-3 rounded-full text-[#74716d]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={24}
                height={24}
                className="text-inherit"
                fill={"none"}
              >
                <path
                  d="M3 7H6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 17H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 17L21 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 7L21 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 7C6 6.06812 6 5.60218 6.15224 5.23463C6.35523 4.74458 6.74458 4.35523 7.23463 4.15224C7.60218 4 8.06812 4 9 4C9.93188 4 10.3978 4 10.7654 4.15224C11.2554 4.35523 11.6448 4.74458 11.8478 5.23463C12 5.60218 12 6.06812 12 7C12 7.93188 12 8.39782 11.8478 8.76537C11.6448 9.25542 11.2554 9.64477 10.7654 9.84776C10.3978 10 9.93188 10 9 10C8.06812 10 7.60218 10 7.23463 9.84776C6.74458 9.64477 6.35523 9.25542 6.15224 8.76537C6 8.39782 6 7.93188 6 7Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 17C12 16.0681 12 15.6022 12.1522 15.2346C12.3552 14.7446 12.7446 14.3552 13.2346 14.1522C13.6022 14 14.0681 14 15 14C15.9319 14 16.3978 14 16.7654 14.1522C17.2554 14.3552 17.6448 14.7446 17.8478 15.2346C18 15.6022 18 16.0681 18 17C18 17.9319 18 18.3978 17.8478 18.7654C17.6448 19.2554 17.2554 19.6448 16.7654 19.8478C16.3978 20 15.9319 20 15 20C14.0681 20 13.6022 20 13.2346 19.8478C12.7446 19.6448 12.3552 19.2554 12.1522 18.7654C12 18.3978 12 17.9319 12 17Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </div>

          {/* You can open the modal using document.getElementById('ID').showModal() method */}
          <dialog id="my_modal_3" className="modal">
            <div className="modal-box phone_modal p-4 pt-14">
              <form method="dialog">
                {/* if there is a button in form, it will close the modal */}
                <button className="btn border border-[#aca9a5] btn-sm btn-circle btn-ghost absolute right-2 top-3 text-[#74716d]">
                  ✕
                </button>
              </form>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <p className="text-[20px] text-[#74716d] font-medium">
                  Where to?
                </p>
                <div>
                  <select
                    onChange={(e) => setLocation(e.target.value)}
                    className="select border-b-2 pl-0 text-[#74716d] border-b-[#aca9a5] h-[50px] rounded-none w-full max-w-lg focus:outline-0 focus:border-b-2 focus:border-b-[#aca9a5]"
                  >
                    <option disabled selected>
                      Where do you want to stay?
                    </option>
                    <option value={"Palm Jumeirah"}>Palm Jumeirah</option>
                    <option value={"Dubai Marina"}>Dubai Marina</option>
                    <option value={"Downtown"}>Downtown</option>
                    <option value={"Bluewaters Island"}>
                      Bluewaters Island
                    </option>
                    <option value={"JBR"}>
                      Jumeirah Beach Residence (JBR)
                    </option>
                    <option value={"Emaar Beachfront"}>Emaar Beachfront</option>
                    <option value={"JVC"}>JVC</option>
                    <option value={"District One"}>District One</option>
                    <option value={""}>Dubai</option>
                  </select>
                </div>
                <Suspense fallback={<div>Loading...</div>}>
                  <CustomDateRangePicker />
                </Suspense>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative lg:w-auto md:w-auto w-full rounded-md h-[52px]">
                    <input
                      type="text"
                      name=""
                      placeholder="Select Guests"
                      id="guest-field"
                      value={getGuestDisplayText()}
                      onClick={() => setVisible(!visible)}
                      className="block min-w-[140px] text-[16px] text-[#74716d] w-full h-[52px] max-w-sm px-3 py-2 border border-gray-300 focus:outline-none focus:ring-[#aca9a5] placeholder:text-[#74716d] focus:border-[#aca9a5] rounded-md"
                    />

                    {visible && (
                      <div
                        id="guest-counter"
                        ref={guestReff2}
                        className="bg-white p-4 shadow-md absolute top-14 w-60 rounded-lg z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <span className="text-[16px] leading-none font-medium text-[#74716d] block">
                                Adult
                              </span>
                              <span className="text-xs text-[#74716d]">
                                Ages 13 or above
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <div
                                onClick={() => handleCounter("minus", "guest")}
                                className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-[#74716d]"
                                  fill={"none"}
                                >
                                  <path
                                    d="M20 12L4 12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <span className="text-[#74716d]">
                                {guestCounts.guest}
                              </span>
                              <div
                                onClick={() => handleCounter("plus", "guest")}
                                className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-[#74716d]"
                                  fill={"none"}
                                >
                                  <path
                                    d="M12 4V20"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M4 12H20"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <span className="text-[16px] leading-none font-medium text-[#74716d] block">
                                Children
                              </span>
                              <span className="text-xs text-[#74716d]">
                                Ages 2 to 12
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <div
                                onClick={() => handleCounter("minus", "child")}
                                className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-[#74716d]"
                                  fill={"none"}
                                >
                                  <path
                                    d="M20 12L4 12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <span className="text-[#74716d]">
                                {guestCounts.child}
                              </span>
                              <div
                                onClick={() => handleCounter("plus", "child")}
                                className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-[#74716d]"
                                  fill={"none"}
                                >
                                  <path
                                    d="M12 4V20"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M4 12H20"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <span className="text-[16px] leading-none font-medium text-[#74716d] block">
                                Infants
                              </span>
                              <span className="text-xs text-[#74716d]">
                                Under 2 years
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <div
                                onClick={() =>
                                  handleCounter("minus", "infants")
                                }
                                className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-[#74716d]"
                                  fill={"none"}
                                >
                                  <path
                                    d="M20 12L4 12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <span className="text-[#74716d]">
                                {guestCounts.infants}
                              </span>
                              <div
                                onClick={() => handleCounter("plus", "infants")}
                                className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-[#74716d]"
                                  fill={"none"}
                                >
                                  <path
                                    d="M12 4V20"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M4 12H20"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>

                          <span
                            onClick={() => setVisible(!visible)}
                            className="text-[#74716d] text-[16px] cursor-pointer mt-4 flex items-center gap-1 hover:text-red-500"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-inherit"
                              fill={"none"}
                            >
                              <path
                                d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Close
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    id="searchBtn"
                    className="mb-[2px] lg:w-auto md:w-auto text-[16px] px-6 rounded-md w-full h-[50px] bg-[#797a76]
        text-white font-normal border-none hover:bg-gray-400"
                  >
                    Find
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        </div>
      </div>
    </section>
  );
};

export default NewBanner;
